import React, { useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';
import { STREAMING_CONFIG } from '../config';
import { dialogsAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';

const WebRTCVideoChat = ({ 
  taskId, 
  videoPath, 
  videoHelloText, 
  coachId, 
  voiceSettings,
  thumbnailUrl,
  onStatusChange,
  onDialogCreated,
  onEnd
}) => {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const audioStreamRef = useRef(null);
  const combinedStreamRef = useRef(new MediaStream());
  const speechTimeoutRef = useRef(null);
  
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeechActive, setIsSpeechActive] = useState(false);
  const [status, setStatus] = useState('Ready');
  const [statusType, setStatusType] = useState('info'); // info, success, error, loading

  const updateStatus = useCallback((msg, type = 'info') => {
    setStatus(msg);
    setStatusType(type);
    if (onStatusChange) onStatusChange(msg, type);
  }, [onStatusChange]);

  const closeConnections = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setIsConnected(false);
    updateStatus('Session ended', 'info');
    combinedStreamRef.current = new MediaStream();
    if (onEnd) onEnd();
  }, [updateStatus, onEnd]);

  const setupAudioWorklet = async (audioContext) => {
    const workletCode = `
      class PCMProcessor extends AudioWorkletProcessor {
        constructor() {
          super();
          this.bufferSize = 2048;
          this.buffer = new Float32Array(this.bufferSize);
          this.bufferIndex = 0;
        }
        process(inputs, outputs) {
          const input = inputs[0];
          if (!input || !input[0]) return true;
          const channelData = input[0];
          for (let i = 0; i < channelData.length; i++) {
            this.buffer[this.bufferIndex++] = channelData[i];
            if (this.bufferIndex >= this.bufferSize) {
              const int16Array = new Int16Array(this.bufferSize);
              for (let j = 0; j < this.bufferSize; j++) {
                const s = Math.max(-1, Math.min(1, this.buffer[j]));
                int16Array[j] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }
              this.port.postMessage(int16Array.buffer, [int16Array.buffer]);
              this.bufferIndex = 0;
            }
          }
          if (outputs && outputs[0] && outputs[0][0]) {
            outputs[0][0].fill(0);
          }
          return true;
        }
      }
      registerProcessor('pcm-processor', PCMProcessor);
    `;

    const blob = new Blob([workletCode], { type: 'application/javascript' });
    const workletUrl = URL.createObjectURL(blob);

    try {
      await audioContext.audioWorklet.addModule(workletUrl);
      const node = new AudioWorkletNode(audioContext, 'pcm-processor');
      node.port.onmessage = (event) => {
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit('audio-chunk', new Uint8Array(event.data));
        }
      };
      return node;
    } finally {
      URL.revokeObjectURL(workletUrl);
    }
  };

  const startConnection = async () => {
    try {
      updateStatus('Connecting to AI server...', 'loading');
      
      // 1. Setup WebRTC
      pcRef.current = new RTCPeerConnection({
        iceServers: STREAMING_CONFIG.ICE_SERVERS,
        bundlePolicy: "max-bundle",
        rtcpMuxPolicy: "require",
        iceCandidatePoolSize: 10,
        iceTransportPolicy: "all"
      });

      // 2. Add Transceivers (recvonly) as per video.html
      pcRef.current.addTransceiver('video', { direction: 'recvonly' });
      pcRef.current.addTransceiver('audio', { direction: 'recvonly' });

      pcRef.current.ontrack = (event) => {
        const receiver = event.receiver;
        if (receiver && 'playoutDelayHint' in receiver) {
          receiver.playoutDelayHint = 0.3; // Align audio and video
        }

        combinedStreamRef.current.addTrack(event.track);
        if (event.track.kind === 'video') {
          if (videoRef.current) {
            videoRef.current.srcObject = combinedStreamRef.current;
            videoRef.current.onloadedmetadata = () => videoRef.current.play().catch(console.error);
          }
          updateStatus('Video stream received', 'success');
        }
      };

      // 3. Connect WebSocket with auth and path
      updateStatus('Initializing dialog...', 'loading');
      let currentDialogId;
      try {
        const dialogRes = await dialogsAPI.create({
            coach_id: coachId,
            user_id: user?.id || 'guest', 
            metadata: { source: "video" }
        });
        
        if (dialogRes.data && dialogRes.data.id) {
            currentDialogId = dialogRes.data.id;
            if (onDialogCreated) onDialogCreated(currentDialogId);
        } else {
            throw new Error('No dialog ID returned');
        }
      } catch (dialogErr) {
        console.error('Failed to create dialog through proxy:', dialogErr);
        // Fallback or handle error
        throw new Error('Failed to create dialog');
      }

      socketRef.current = io(STREAMING_CONFIG.WEBSOCKET_SERVER_URL, {
        path: STREAMING_CONFIG.WEBSOCKET_PATH,
        auth: { token: STREAMING_CONFIG.AUTH_TOKEN },
        query: {
            task_id: taskId,
            dialog_id: currentDialogId,
            settings_id: voiceSettings || coachId 
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
      });

      await new Promise((resolve, reject) => {
        socketRef.current.on('connect', resolve);
        socketRef.current.on('connect_error', reject);
        setTimeout(() => reject(new Error('WebSocket timeout')), 10000);
      });

      // Listen for speech recognition events
      const handleSpeech = () => {
        setIsSpeechActive(true);
        if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = setTimeout(() => {
          setIsSpeechActive(false);
        }, 1500);
      };

      socketRef.current.on('speech-start', handleSpeech);
      socketRef.current.on('vad-start', handleSpeech);
      socketRef.current.on('recognition-result', handleSpeech);
      socketRef.current.on('speech-end', () => {
        setIsSpeechActive(false);
        if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
      });
      socketRef.current.on('vad-end', () => {
        setIsSpeechActive(false);
        if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
      });

      // 4. Setup Local Audio (for recognition)
      audioStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true }
      });
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      workletNodeRef.current = await setupAudioWorklet(audioContextRef.current);
      
      const source = audioContextRef.current.createMediaStreamSource(audioStreamRef.current);
      source.connect(workletNodeRef.current);
      workletNodeRef.current.connect(audioContextRef.current.destination);

      // 5. WebRTC Offer
      const offer = await pcRef.current.createOffer();
      await pcRef.current.setLocalDescription(offer);

      // Wait for ICE gathering
      updateStatus('Gathering ICE candidates...', 'loading');
      await new Promise(resolve => {
        if (pcRef.current.iceGatheringState === 'complete') {
          resolve();
        } else {
          const checkState = () => {
            if (pcRef.current?.iceGatheringState === 'complete') {
              pcRef.current.removeEventListener('icegatheringstatechange', checkState);
              resolve();
            }
          };
          pcRef.current.addEventListener('icegatheringstatechange', checkState);
          setTimeout(resolve, 2000); // 2s timeout as in video.html
        }
      });

      // 6. Send Offer to WebRTC API
      const response = await fetch(`${STREAMING_CONFIG.API_URL}/api/webrtc/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sdp: pcRef.current.localDescription.sdp,
          type: pcRef.current.localDescription.type,
          task_id: taskId,
          video_path: videoPath,
          audio_path: 'base', 
          version: STREAMING_CONFIG.VERSION,
          fps: STREAMING_CONFIG.FPS,
          batch_size: STREAMING_CONFIG.BATCH_SIZE,
          video_hello_text: videoHelloText,
          settings_id: voiceSettings || coachId 
        })
      });

      if (!response.ok) throw new Error('WebRTC server rejected offer');
      const answer = await response.json();
      await pcRef.current.setRemoteDescription(new RTCSessionDescription(answer));

      // 7. Start Recognition
      socketRef.current.emit('start-recognition', { 
        languageCode: STREAMING_CONFIG.DEFAULT_LANGUAGE 
      });

      setIsConnected(true);
      updateStatus('Connected and ready', 'success');

    } catch (err) {
      console.error('Connection error:', err);
      updateStatus(err.message, 'error');
      closeConnections();
    }
  };

  useEffect(() => {
    startConnection();
    return () => closeConnections();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative w-full h-full bg-black flex items-center justify-center overflow-hidden">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="w-full h-full object-cover relative z-1"
        style={{ opacity: isConnected ? 1 : 0, transition: 'opacity 1s' }}
      />

      {/* Background Thumbnail while connecting */}
      {!isConnected && thumbnailUrl && (
        <img 
          src={thumbnailUrl} 
          alt="" 
          className="absolute inset-0 w-full h-full object-cover opacity-50 z-0"
        />
      )}
      
      {!isConnected && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm z-10">
          {statusType === 'loading' && (
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--tangerine)] mb-4"></div>
          )}
          {(statusType === 'error' || status === 'Session ended') && (
             <button
               onClick={startConnection}
               className={`${status === 'Session ended' ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-[var(--tangerine)] shadow-orange-500/20'} text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest shadow-2xl hover:scale-105 transition-all flex items-center gap-3`}
             >
               <span className="material-symbols-outlined text-2xl">
                 {status === 'Session ended' ? 'play_circle' : 'refresh'}
               </span>
               {status === 'Session ended' ? 'Resume Session' : 'Retry Connection'}
             </button>
          )}
          <p className={`mt-4 text-xs font-bold uppercase tracking-widest ${
            statusType === 'error' ? 'text-red-400' : 'text-white/60'
          }`}>
            {status}
          </p>
        </div>
      )}

      {isConnected && (
        <div className="absolute bottom-10 left-0 right-0 flex justify-center z-20">
          <div className={`speech-active-container ${isSpeechActive ? 'active' : ''}`}>
            <button
              onClick={closeConnections}
              className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center text-white shadow-xl hover:scale-110 active:scale-95 transition-all relative z-10"
            >
              <span className="material-symbols-outlined text-3xl">call_end</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebRTCVideoChat;
